// Which screens a tenant can see.
//
// Extracted from SideMenu so it can be tested without rendering the component
// (SideMenu reads auth and RTDB on mount, which is why the repo's only
// component test was deleted rather than repaired). Same reason rosterParser,
// tmAuth and pushTargeting were pulled out: the decision is here, the wiring
// stays in the component.
//
// Two things this protects:
//   1. Every screen is reachable for the tenants that are meant to have it.
//      A flag typo silently removes a whole screen from the admin with no
//      error anywhere.
//   2. No group renders an orphaned heading. The nav is a data structure so
//      a fully gated-off group can be dropped, and that only holds if some
//      item in every group is always visible for every flag combination.

// Tenants that were brought onto the newer config screens. Anyone not in this
// list has no Home Buttons / Side Menu Buttons / Bottom Tabs / Settings.
const NEW_FEATURE_PROJECTS = [
    'coachella-firebirds-app',
    'anothertestdb-be6c5',
    'checkersdbtest',
    'whawksv2'
];

// Send Rewards mails fans directly and is deliberately narrower than Rewards.
const SEND_REWARDS_PROJECTS = ['anothertestdb-be6c5', 'whawksv2'];

const isTrue = (v) => v === 'true';

/**
 * @param env      process.env, or a tenant's parsed .env for tests
 * @param isGod            whitelist hit — from RTDB at runtime
 * @param bottomTabsEnabled — from RTDB at runtime
 * @param features         which feature flags are switched on for somebody —
 *                         from RTDB at runtime, see utils/featureReachability
 * @param hasExperiences   whether the club has any experiences configured —
 *                         a different question from whether fans may switch
 */
export function buildNavGroups(env = {}, { isGod = false, bottomTabsEnabled = false, features = {}, hasExperiences = false } = {}) {
    const projectId = env.REACT_APP_FIREBASE_PROJECT_ID || '';

    const playerPick = isTrue(env.REACT_APP_HAS_PLAYER_PICK);
    const checkIn = isTrue(env.REACT_APP_HAS_CHECK_IN);
    const prizes = isTrue(env.REACT_APP_HAS_PRIZES);
    const push = isTrue(env.REACT_APP_HAS_PUSH_NOTIFICATIONS);
    const badges = isTrue(env.REACT_APP_HAS_BADGES);
    const radio = isTrue(env.REACT_APP_HAS_RADIO);
    // Offers, experiences and season-ticket verification are no longer decided
    // by a build-time env flag. They are the same switches fans are gated on,
    // in Feature Access, so a club turns one on for itself and the screen that
    // configures it arrives with it — nobody has to ask us to rebuild the admin
    // to try a feature we have just announced.
    //
    // "Switched on for somebody" (not "on for everyone") on purpose: the way to
    // set a feature up before fans meet it is to switch it on for yourself
    // first, which is exactly what the Feature Access screen recommends. That
    // path only works if the screen you need appears at that point.
    const offers = features.offers === true;
    // NOT just the switcher. `experienceSwitcher` answers "can fans CHOOSE
    // between experiences" — it does not answer "does this club use them".
    // Gating the admin screen on it meant an operator who turned the switcher
    // off, which is how you say "everyone gets the primary, no menu", lost the
    // screen that manages experiences — including the way to promote a
    // different one back. The same shape of trap ran the other way too: a club
    // with no experiences had no reason to switch a fan-facing menu on, and so
    // could never reach the screen that would create their first one.
    //
    // So: show it while the club HAS experiences, or while someone has opted
    // in. A club that has never used them still ships with it hidden.
    const experiences = features.experienceSwitcher === true || hasExperiences === true;
    const verification = features.verification === true;
    const activationsUrl = env.REACT_APP_ACTIVATIONS_URL;

    const hasNewFeatures = NEW_FEATURE_PROJECTS.indexOf(projectId) !== -1;
    const canSendRewards = prizes && SEND_REWARDS_PROJECTS.indexOf(projectId) !== -1;

    return [
        {
            label: 'Overview',
            items: [
                { title: 'Dashboard', href: '/admin', icon: 'fa-home', show: true }
            ]
        },
        {
            label: 'Game night',
            items: [
                { title: 'Games', href: '/setupgame', icon: 'fa-gamepad', show: playerPick || checkIn },
                // Check-in reads pointRules/distance and pointRules/perCheckin, and
                // this screen is the only writer of either. Gated on playerPick alone,
                // a check-in tenant could never set them: distance stayed unset and
                // every fan got "Too Far From The Stadium" forever.
                { title: 'Edit Points', href: '/pointvalues', icon: 'fa-plus', show: playerPick || checkIn },
                { title: 'Players', href: '/manageplayers', icon: 'fa-clipboard', show: playerPick },
                { title: 'Radio', href: '/radio', icon: 'fa-microphone', show: radio }
            ]
        },
        {
            label: 'Fan engagement',
            items: [
                { title: 'Rewards', href: '/setuptickets', icon: 'fa-trophy', show: prizes },
                { title: 'Send Rewards', href: '/sendprizes', icon: 'fa-envelope', show: canSendRewards },
                { title: 'Badges', href: '/setupbadges', icon: 'fa-certificate', show: badges },
                { title: 'Offers', href: '/offers', icon: 'fa-gift', show: offers },
                { title: 'Push Notifications', href: '/pushnotifications', icon: 'fa-bullhorn', show: push },
                { title: 'Activations', href: activationsUrl, icon: 'fa-star', show: !!activationsUrl, external: true }
            ]
        },
        {
            label: 'App layout',
            items: [
                { title: 'Top Carousel', href: '/topcarousel', icon: 'fa-tv', show: true },
                { title: 'Home Buttons', href: '/homebuttons', icon: 'fa-sort-amount-asc', show: hasNewFeatures },
                { title: 'Side Menu Buttons', href: '/sidemenubuttons', icon: 'fa-list', show: hasNewFeatures },
                { title: 'Bottom Tabs', href: '/bottomtabs', icon: 'fa-ellipsis-h', show: hasNewFeatures && bottomTabsEnabled },
                { title: 'Experiences', href: '/experiences', icon: 'fa-clone', show: experiences }
            ]
        },
        {
            label: 'Access',
            items: [
                { title: 'User Management', href: '/usermanagement', icon: 'fa-user', show: true },
                { title: 'Admin Management', href: '/adminManagement', icon: 'fa-user-secret', show: isGod },
                { title: 'Verification', href: '/verification', icon: 'fa-id-card', show: verification },
                // Not experience-gated: feature access controls surfaces for every tenant
                { title: 'Feature Access', href: '/featureaccess', icon: 'fa-toggle-on', show: true },
                { title: 'Settings', href: '/settings', icon: 'fa-gear', show: hasNewFeatures }
            ]
        }
    ];
}

// Groups that will actually render, with their gated-off items removed.
export function visibleNavGroups(env, flags) {
    return buildNavGroups(env, flags)
        .map(group => ({ label: group.label, items: group.items.filter(item => item.show) }))
        .filter(group => group.items.length > 0);
}
